import React, { useEffect, useMemo, useState } from 'react';
import L, { LatLngExpression } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'react-leaflet-markercluster/dist/styles.min.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

const GEOCODE_CACHE_KEY = 'deviceCityGeocodeCache_v2'; // v2 = localStorage (was sessionStorage)
const DEFAULT_CENTER: LatLngExpression = [52.1326, 5.2913]; // Netherlands
const DEFAULT_ZOOM = 6;
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

type DeviceLocationMapRegistration = {
    id: string;
    serialNumber: string;
    customer?: string;
    clientCity?: string;
    lastSeenAt?: string;
    registeredAt?: string;
};

type ResolvedCityEntry = {
    cityKey: string;
    city: string;
    registrations: DeviceLocationMapRegistration[];
    geocode: GeocodeResult;
};

type GeocodeResult = {
    lat: number;
    lon: number;
    displayName: string;
};

type GeocodeCache = Record<string, GeocodeResult>;

type DeviceLocationsMapProps = {
    registrations: DeviceLocationMapRegistration[];
    layout?: 'default' | 'stacked';
};

const normalizeCity = (value: string): string => value.trim().toLowerCase();

const parseDateSafe = (value?: string): number => {
    if (!value) {
        return 0;
    }

    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getClusterIcon = (totalDevices: number): L.DivIcon =>
    L.divIcon({
        className: 'device-marker-icon',
        html: `<span class="device-marker-dot has-count" aria-hidden="true"><span class="device-marker-count">${totalDevices}</span></span>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -24],
    });

const getCityMarkerIcon = (deviceCount: number): L.DivIcon => {
    const hasCount = deviceCount > 1;
    return L.divIcon({
        className: 'device-marker-icon',
        html: `<span class="device-marker-dot${hasCount ? ' has-count' : ''}" aria-hidden="true">${hasCount ? `<span class="device-marker-count">${deviceCount}</span>` : ''}</span>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -24],
    });
};

const safeParseCache = (): GeocodeCache => {
    const raw = localStorage.getItem(GEOCODE_CACHE_KEY);
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw) as GeocodeCache;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const saveCache = (cache: GeocodeCache): void => {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Module-level deduplication: prevents parallel requests even when the effect
// fires multiple times (React StrictMode, rapid re-renders, etc.)
const inFlightCities = new Set<string>();
let geocodeQueueRunning = false;

const geocodeCity = async (city: string): Promise<GeocodeResult | null> => {
    const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(city)}`,
        {
            headers: {
                'Accept-Language': 'en',
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to geocode city: ${city}`);
    }

    const results = (await response.json()) as Array<{ lat: string; lon: string; display_name: string }>;
    if (!results.length) {
        return null;
    }

    const first = results[0];
    const lat = Number.parseFloat(first.lat);
    const lon = Number.parseFloat(first.lon);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
    }

    return {
        lat,
        lon,
        displayName: first.display_name,
    };
};

const FitMapToMarkers: React.FC<{ positions: LatLngExpression[] }> = ({ positions }) => {
    const map = useMap();

    useEffect(() => {
        if (!positions.length) {
            return;
        }

        if (positions.length === 1) {
            map.setView(positions[0], 10);
            return;
        }

        const bounds = L.latLngBounds(positions as [number, number][]);
        map.fitBounds(bounds, { padding: [30, 30] });
    }, [map, positions]);

    return null;
};

const DeviceLocationsMap: React.FC<DeviceLocationsMapProps> = ({ registrations, layout = 'default' }) => {
    const [geocodeByCity, setGeocodeByCity] = useState<GeocodeCache>({});
    const [isGeocoding, setIsGeocoding] = useState<boolean>(false);
    const [geocodeError, setGeocodeError] = useState<string>('');

    const groupedByCity = useMemo(() => {
        const groups = new Map<string, { city: string; registrations: DeviceLocationMapRegistration[] }>();

        registrations.forEach((registration) => {
            const city = registration.clientCity?.trim();
            if (!city) {
                return;
            }

            const key = normalizeCity(city);
            const existing = groups.get(key);
            if (existing) {
                existing.registrations.push(registration);
                return;
            }

            groups.set(key, {
                city,
                registrations: [registration],
            });
        });

        return Array.from(groups.entries()).map(([key, value]) => ({
            cityKey: key,
            city: value.city,
            registrations: value.registrations,
        }));
    }, [registrations]);

    useEffect(() => {
        const cached = safeParseCache();
        setGeocodeByCity(cached);
    }, []);

    useEffect(() => {
        if (!groupedByCity.length) {
            return;
        }

        let cancelled = false;
    let ownedQueue = false;

        const loadMissingCities = async () => {
            setGeocodeError('');
            const cache = safeParseCache();
            const missing = groupedByCity.filter(
                (entry) => !cache[entry.cityKey]
            );

            if (!missing.length) {
                if (!cancelled) setGeocodeByCity(safeParseCache());
                return;
            }

            // If another loop is already running, let it handle the queue
            if (geocodeQueueRunning) {
                if (!cancelled) setGeocodeByCity(safeParseCache());
                return;
            }

            geocodeQueueRunning = true;
            ownedQueue = true;
            missing.forEach((e) => inFlightCities.add(e.cityKey));

            if (!cancelled) {
                setIsGeocoding(true);
                setGeocodeByCity({ ...cache }); // Show cached dots immediately
            }

            let updatedCache: GeocodeCache = { ...cache };

            try {
                for (let i = 0; i < missing.length; i++) {
                    if (cancelled) break;
                    const entry = missing[i];
                    if (i > 0) {
                        await sleep(1100); // Nominatim: max 1 request/sec
                        if (cancelled) break;
                    }
                    try {
                        const result = await geocodeCity(entry.city);
                        if (!cancelled && result) {
                            updatedCache = { ...updatedCache, [entry.cityKey]: result };
                            saveCache(updatedCache);
                            setGeocodeByCity(updatedCache);
                        }
                    } catch {
                        // Skip unresolvable cities
                    } finally {
                        inFlightCities.delete(entry.cityKey);
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    const message = error instanceof Error ? error.message : 'Failed to resolve one or more cities';
                    setGeocodeError(message);
                }
            } finally {
                geocodeQueueRunning = false;
                if (!cancelled) setIsGeocoding(false);
            }
        };

        loadMissingCities();

        return () => {
            cancelled = true;
            if (ownedQueue) {
                geocodeQueueRunning = false;
                ownedQueue = false;
            }
        };
    }, [groupedByCity]);

    const resolvedLocations = useMemo(() => {
        return groupedByCity
            .map((entry) => ({
                ...entry,
                geocode: geocodeByCity[entry.cityKey],
            }))
            .filter((entry): entry is ResolvedCityEntry => Boolean(entry.geocode));
    }, [groupedByCity, geocodeByCity]);

    const totalDevices = useMemo(
        () => resolvedLocations.reduce((sum, entry) => sum + entry.registrations.length, 0),
        [resolvedLocations]
    );

    const summaryStats = useMemo(() => {
        const serialLastSeen = new Map<string, number>();
        const serialRegisteredAt = new Map<string, number>();
        const serialsWithCity = new Set<string>();

        registrations.forEach((registration) => {
            const serial = registration.serialNumber?.trim();
            if (!serial) {
                return;
            }

            const seenAt = parseDateSafe(registration.lastSeenAt);
            const knownSeenAt = serialLastSeen.get(serial) ?? 0;
            if (seenAt > knownSeenAt) {
                serialLastSeen.set(serial, seenAt);
            }

            const registeredAt = parseDateSafe(registration.registeredAt);
            const knownRegisteredAt = serialRegisteredAt.get(serial) ?? 0;
            if (registeredAt > knownRegisteredAt) {
                serialRegisteredAt.set(serial, registeredAt);
            }

            if (registration.clientCity?.trim()) {
                serialsWithCity.add(serial);
            }
        });

        const uniqueSerialCount = serialLastSeen.size || serialRegisteredAt.size;
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

        let online24h = 0;
        let online7d = 0;
        let neverSeenOnline = 0;

        serialLastSeen.forEach((lastSeenAt) => {
            if (lastSeenAt === 0) {
                neverSeenOnline += 1;
                return;
            }

            const age = now - lastSeenAt;
            if (age <= oneDayMs) {
                online24h += 1;
            }

            if (age <= sevenDaysMs) {
                online7d += 1;
            }
        });

        // If a serial only exists in registeredAt map, it effectively has never been seen online.
        neverSeenOnline += Math.max(uniqueSerialCount - serialLastSeen.size, 0);

        let registeredLast7d = 0;
        serialRegisteredAt.forEach((registeredAt) => {
            if (registeredAt > 0 && now - registeredAt <= sevenDaysMs) {
                registeredLast7d += 1;
            }
        });

        const unresolvedCities = groupedByCity.length - resolvedLocations.length;
        const devicesPerResolvedCity = resolvedLocations.length > 0
            ? (totalDevices / resolvedLocations.length).toFixed(1)
            : '0.0';

        return {
            uniqueSerialCount,
            online24h,
            online7d,
            neverSeenOnline,
            registeredLast7d,
            withKnownCity: serialsWithCity.size,
            resolvedCities: resolvedLocations.length,
            unresolvedCities,
            devicesPerResolvedCity,
        };
    }, [registrations, groupedByCity.length, resolvedLocations, totalDevices]);

    const markerPositions = useMemo(
        () => resolvedLocations.map((entry) => [entry.geocode.lat, entry.geocode.lon] as [number, number]),
        [resolvedLocations]
    );

    return (
        <section className="device-map-panel">
            <div className="device-map-panel-header">
                <h3>Device locations</h3>
                <p>
                    {totalDevices} devices in {resolvedLocations.length} of {groupedByCity.length} cities resolved
                    {isGeocoding ? ' (resolving...)' : ''}
                </p>
            </div>
            {geocodeError && <p className="error-text">{geocodeError}</p>}
            <div className={`device-map-layout${layout === 'stacked' ? ' device-map-layout-stacked' : ''}`}>
                <div className="device-map-container" role="region" aria-label="Map with registered device locations">
                    {groupedByCity.length > 0 ? (
                        <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} className="device-map" scrollWheelZoom>
                            <TileLayer
                                attribution={TILE_ATTRIBUTION}
                                url={TILE_URL}
                                subdomains="abc"
                                referrerPolicy="strict-origin-when-cross-origin"
                            />
                            <FitMapToMarkers positions={markerPositions} />
                            <MarkerClusterGroup
                                chunkedLoading
                                showCoverageOnHover={false}
                                maxClusterRadius={48}
                                {...({
                                    iconCreateFunction: (cluster: { getAllChildMarkers: () => L.Marker[] }) => {
                                        const total = (cluster.getAllChildMarkers() as Array<L.Marker & { deviceCount?: number }>).reduce(
                                            (sum: number, marker) => sum + (marker.deviceCount ?? 1),
                                            0,
                                        );
                                        return getClusterIcon(total);
                                    },
                                } as object)}
                            >
                                {resolvedLocations.map((entry) => (
                                    <Marker
                                        key={entry.cityKey}
                                        position={[entry.geocode.lat, entry.geocode.lon]}
                                        icon={getCityMarkerIcon(entry.registrations.length)}
                                        ref={(markerRef) => {
                                            if (markerRef) {
                                                (markerRef as unknown as { deviceCount: number }).deviceCount =
                                                    entry.registrations.length;
                                            }
                                        }}
                                    >
                                        <Popup>
                                            <strong>{entry.city}</strong>
                                            <br />
                                            {entry.registrations.length} device(s)
                                            <br />
                                            {entry.registrations
                                                .slice(0, 5)
                                                .map((registration) => registration.serialNumber)
                                                .join(', ')}
                                            {entry.registrations.length > 5 ? ' ...' : ''}
                                        </Popup>
                                    </Marker>
                                ))}
                            </MarkerClusterGroup>
                        </MapContainer>
                    ) : (
                        <div className="device-map-empty-state">No city information available yet.</div>
                    )}
                </div>
                <aside className="device-map-summary" aria-label="Device location summary">
                    <h4>Summary</h4>
                    <table className="device-map-summary-table">
                        <tbody>
                            <tr>
                                <th scope="row">Registered serials</th>
                                <td><span className="metric-pill metric-neutral">{summaryStats.uniqueSerialCount}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Online in 24h</th>
                                <td><span className="metric-pill metric-good">{summaryStats.online24h}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Online in 7 days</th>
                                <td><span className="metric-pill metric-good-soft">{summaryStats.online7d}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Registered in last 7 days</th>
                                <td><span className="metric-pill metric-info">{summaryStats.registeredLast7d}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Serials with city</th>
                                <td><span className="metric-pill metric-info">{summaryStats.withKnownCity}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Resolved cities</th>
                                <td><span className="metric-pill metric-good-soft">{summaryStats.resolvedCities}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Unresolved cities</th>
                                <td><span className="metric-pill metric-warn">{summaryStats.unresolvedCities}</span></td>
                            </tr>
                            <tr>
                                <th scope="row">Avg devices per city</th>
                                <td><span className="metric-pill metric-neutral">{summaryStats.devicesPerResolvedCity}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </aside>
            </div>
        </section>
    );
};

export default DeviceLocationsMap;
